/**
 * SovereigntyStreamTicker — Bloomberg-style scrolling ticker of
 * anonymised redaction events platform-wide.  No auth.  Polls every
 * 20s, animates the row infinitely from right → left using CSS
 * keyframes.  Designed for the public homepage + /security page.
 *
 * Each event is a high-level bucket (PII · TRADE-SECRET · LOCATION ·
 * LINK · OTHER · SCAN) — never a sample, never per-tenant.
 *
 * Layout: single-line marquee, ~40px tall.  Respects
 * prefers-reduced-motion (stops animation, shows static newest 5).
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Activity } from 'lucide-react';
import api from '@/lib/api';

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60)   return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
}

function colorForKind(kind) {
  if (kind?.includes('TRADE-SECRET')) return '#F59E0B';
  if (kind?.includes('PII'))           return '#34D399';
  if (kind?.includes('LOCATION'))      return '#60A5FA';
  if (kind?.includes('LINK'))          return '#C77CFF';
  if (kind === 'SCAN')                 return '#9CA3AF';
  return '#AE52EA';
}

export default function SovereigntyStreamTicker({ className = '' }) {
  const [events, setEvents] = useState([]);
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    queueMicrotask(() => {
      setReduce(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    });
    let cancel = false;
    const load = () => {
      api.get('/privacy/sovereignty-stream?limit=30')
        .then(({ data }) => {
          if (cancel) return;
          queueMicrotask(() => setEvents(Array.isArray(data) ? data : []));
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 20000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  if (!events.length) return null;

  // For an unbroken loop we duplicate the events array so the marquee
  // can translate -50% before resetting back to 0%.
  const loop = [...events, ...events];

  return (
    <div
      className={`relative overflow-hidden rounded-md border border-emerald-400/30 bg-emerald-500/[0.04] ${className}`}
      data-testid="sovereignty-stream-ticker"
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className="shrink-0 flex items-center gap-2 pr-3 border-r border-emerald-400/20">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-600 dark:text-emerald-300 whitespace-nowrap">
            Sovereign Stream
          </span>
        </div>

        {reduce ? (
          <div className="flex items-center gap-5 overflow-x-auto pal-mono text-[11px]" data-testid="sovereignty-stream-static">
            {events.slice(0, 5).map((e, i) => (
              <TickItem key={i} ev={e} />
            ))}
          </div>
        ) : (
          <div className="relative min-w-0 flex-1 overflow-hidden">
            <div className="ve-ticker-track flex items-center gap-8 pal-mono text-[11px] whitespace-nowrap">
              {loop.map((e, i) => (
                <TickItem key={i} ev={e} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TickItem({ ev }) {
  const color = colorForKind(ev.kind);
  const Icon = ev.kind === 'SCAN' ? Activity : ShieldCheck;
  return (
    <span
      className="inline-flex items-center gap-2 shrink-0"
      data-testid="sovereignty-stream-item"
    >
      <Icon size={11} style={{ color }} />
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color }}>
        {ev.kind}
      </span>
      <span className="text-slate-800 dark:text-white/85">×{ev.count}</span>
      <span className="text-slate-500 dark:text-white/45">· {timeAgo(ev.at)}</span>
      <span className="text-emerald-700 dark:text-emerald-300/70">via {ev.via}</span>
    </span>
  );
}
